import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  AttentionResponse,
  OrgAttentionQuery,
  OrgAttentionResponse,
} from "@/types/attention.types";

export type {
  AttentionOccurrence,
  AttentionResponse,
  OrgAttentionQuery,
  OrgAttentionResponse,
} from "@/types/attention.types";

/**
 * Unified attention view for a line: active alarms + pending stop justifications
 * + no-data gaps, co-located. Invalidated via ["attention"] by useRealtimeInvalidate
 * and the alarm/event mutations.
 */
export function useLineAttention(lineId: string, refetchInterval = 30_000) {
  return useQuery<AttentionResponse>({
    queryKey: ["attention", lineId],
    queryFn: () => api.get<AttentionResponse>(`/api/v1/lines/${lineId}/attention`),
    enabled: !!lineId,
    refetchInterval,
  });
}

export type OrgAttentionParams = Partial<OrgAttentionQuery>;

function orgAttentionQs(query: OrgAttentionParams): string {
  const search = new URLSearchParams();
  if (query.scope) search.set("scope", query.scope);
  if (query.lineId) search.set("lineId", query.lineId);
  if (query.type) search.set("type", query.type);
  // Filtros de severidade e oscilação.
  if (query.severity) search.set("severity", query.severity);
  if (query.flapping) search.set("flapping", "true");
  if (query.limit != null) search.set("limit", String(query.limit));
  // Paginação server-side.
  if (query.offset != null) search.set("offset", String(query.offset));
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Org-wide unified attention (all lines) — feeds the consolidated /alarms page.
 * queryKey ["attention","org",query]; invalidated by ["attention"]
 * via the alarm/event mutations and useRealtimeInvalidate.
 */
export function useOrgAttention(query: OrgAttentionParams = {}, refetchInterval = 30_000) {
  return useQuery<OrgAttentionResponse>({
    queryKey: ["attention", "org", query],
    queryFn: () => api.get<OrgAttentionResponse>(`/api/v1/attention${orgAttentionQs(query)}`),
    refetchInterval,
    // Mantém a página anterior montada enquanto a próxima carrega (sem o pager
    // sumir/piscar ao paginar). Combinado com derivar offset de `data.offset`, evita pular página.
    placeholderData: (prev) => prev,
  });
}
